(function () {
  'use strict';
  // ─── 状态管理 ────────────────────────────────────────────
  const cache = {};
  const loading = new Set();
  let currentController = null;
  let hoverTimer = null;
  const HOVER_DELAY = 80;
  const FADE_DURATION = 150;
  // ─── 工具函数 ────────────────────────────────────────────
  function isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function resizeImage(src, size) {
    if (!src) return '';
    return src.replace(/(\.[^.?]+)(\?.*)?$/, `_${size}$1$2`);
  }
  function formatMoney(priceInCents) {
    if (priceInCents == null || priceInCents === '') return '';
    if (window.FoxTheme && window.FoxTheme.Currency && typeof window.FoxTheme.Currency.formatMoney === 'function') {
      const moneyFormat = window.FoxTheme.settings && window.FoxTheme.settings.moneyFormat;
      return window.FoxTheme.Currency.formatMoney(priceInCents, moneyFormat);
    }
    const numericPrice = Number(priceInCents);
    if (Number.isNaN(numericPrice)) return '';
    return `$${(numericPrice / 100).toFixed(2)}`;
  }
  function getProductPricing(product) {
    if (!product) return null;
    const firstAvailableVariant = product.variants && product.variants.find((variant) => variant.available);
    const baseVariant = firstAvailableVariant || (product.variants && product.variants[0]) || null;

    let price = baseVariant && baseVariant.price != null ? baseVariant.price : product.price;
    if (price == null || price === '') price = product.price_min;

    let compareAtPrice = baseVariant && baseVariant.compare_at_price != null
      ? baseVariant.compare_at_price
      : product.compare_at_price;
    if (compareAtPrice == null || compareAtPrice === '') compareAtPrice = product.compare_at_price_max;

    const numericPrice = Number(price);
    const numericCompareAtPrice = Number(compareAtPrice);
    const onSale =
      !Number.isNaN(numericPrice)
      && !Number.isNaN(numericCompareAtPrice)
      && numericCompareAtPrice > numericPrice;

    return {
      price: formatMoney(price),
      compareAtPrice: onSale ? formatMoney(compareAtPrice) : '',
      onSale,
    };
  }
  // 图片预加载处理，缓存图片直接标记loaded
  function waitImagesLoaded(wrapper) {
    const imgs = wrapper.querySelectorAll('img');
    return Promise.all(Array.from(imgs).map(img => {
      if (img.complete) {
        img.classList.add('loaded');
        return Promise.resolve();
      }
      return new Promise(res => img.onload = () => {
        img.classList.add('loaded');
        res();
      });
    }));
  }
  // 平滑替换容器，不整体淡出留白
  function softSwapContainer(container, newHtml) {
    if (prefersReducedMotion()) {
      container.innerHTML = newHtml;
      return Promise.resolve();
    }
    return new Promise(resolve => {
      container.style.opacity = '0.6';
      setTimeout(async () => {
        const temp = document.createElement('div');
        temp.innerHTML = newHtml;
        await waitImagesLoaded(temp);
        container.innerHTML = temp.innerHTML;
        container.style.opacity = '1';
        setTimeout(resolve, FADE_DURATION);
      }, FADE_DURATION / 2);
    });
  }
  // 生成商品HTML，只返回字符串不操作DOM
  function renderProductsHtml(products) {
    if (!products || !products.length) {
      return '<p class="mega-menu__dynamic-empty">暂无产品</p>';
    }
    return products.slice(0, 4).map((p, i) => {
      const rawSrc = p.images && p.images[0] ? p.images[0].src : '';
      const imgSrc = resizeImage(rawSrc, '300x300');
      const title  = escapeHtml(p.title);
      const handle = escapeHtml(p.handle);
      const pricing = getProductPricing(p);
      return `
        <a
          href="/products/${handle}"
          class="promotion-item mega-dynamic-card"
          style="--index:${i + 1}"
        >
          ${imgSrc
            ? `<img
                src="${imgSrc}"
                alt="${title}"
                loading="eager"
                width="300"
                height="300"
                style="width:100%;aspect-ratio:1/1;object-fit:cover;border-radius:4px;"
              >`
            : '<div class="mega-dynamic-card__no-image"></div>'
          }
          <p class="mega-dynamic-card__title">${title}</p>
          ${pricing && pricing.price
            ? `<p class="mega-dynamic-card__price">
                <span class="mega-dynamic-card__price-current${pricing.onSale ? ' is-sale' : ''}">${pricing.price}</span>
                ${pricing.onSale && pricing.compareAtPrice
                  ? `<span class="mega-dynamic-card__price-compare"><s>${pricing.compareAtPrice}</s></span>`
                  : ''
                }
              </p>`
            : ''
          }
        </a>
      `;
    }).join('');
  }
  // 加载商品核心
  async function loadProducts(handle, container) {
    // 已有缓存直接渲染，不显示Loading文字
    if (cache[handle]) {
      const html = renderProductsHtml(cache[handle]);
      await softSwapContainer(container, html);
      return;
    }
    if (loading.has(handle)) return;
    if (currentController) currentController.abort();
    currentController = new AbortController();
    const signal = currentController.signal;
    loading.add(handle);

    // 仅无缓存时才展示Loading
    container.innerHTML = '<div class="mega-menu__dynamic-loading">Loading...</div>';
    try {
      const res = await fetch(`/collections/${handle}/products.json?limit=4`, { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      cache[handle] = data.products;
      const html = renderProductsHtml(data.products);
      await softSwapContainer(container, html);
    } catch (e) {
      if (e.name === 'AbortError') {
        loading.delete(handle);
        return;
      }
      console.warn('[MegaMenu] 加载失败:', e);
      container.innerHTML = '<div class="mega-menu__dynamic-empty">加载失败</div>';
    } finally {
      loading.delete(handle);
    }
  }
  // 页面初始化预加载每个菜单第一个分类，提前缓存（手机PC通用）
  function prefetchAllFirstCategory() {
    document.querySelectorAll('details[is="details-mega"]').forEach(details => {
      const firstLink = details.querySelector('a[data-collection-handle]');
      if (!firstLink) return;
      const handle = firstLink.dataset.collectionHandle;
      if (!handle || cache[handle] || loading.has(handle)) return;
      loading.add(handle);
      fetch(`/collections/${handle}/products.json?limit=4`)
        .then(r => r.json())
        .then(data => { cache[handle] = data.products; })
        .catch(() => {})
        .finally(() => loading.delete(handle));
    });
  }
  // 菜单展开立刻加载首分类 —— 移除手机阻断，移动端点击菜单也自动出图
  function loadFirstItemImmediately(detailsEl) {
    const links = detailsEl.querySelectorAll('a[data-collection-handle]');
    const container = detailsEl.querySelector('.mega-menu__dynamic-products');
    if (!links.length || !container) return;
    const firstLink = links[0];
    const handle = firstLink.dataset.collectionHandle;
    if (!handle) return;
    // 激活第一个分类样式
    links.forEach(l => l.classList.remove('is-active'));
    firstLink.classList.add('is-active');
    // 直接加载，手机/PC打开菜单立刻渲染商品
    loadProducts(handle, container);
  }
  // 初始化分类切换事件：PC鼠标hover + 手机touch点击双兼容
  function initDynamic(detailsEl) {
    // 移除移动端阻断，手机也初始化切换逻辑
    const links = detailsEl.querySelectorAll('a[data-collection-handle]');
    const container = detailsEl.querySelector('.mega-menu__dynamic-products');
    if (!links.length || !container) return;
    
    links.forEach(link => {
      // PC悬浮事件
      link.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
          links.forEach(l => l.classList.remove('is-active'));
          link.classList.add('is-active');
          const handle = link.dataset.collectionHandle;
          if (!handle) return;
          loadProducts(handle, container);
        }, HOVER_DELAY);
      });
      link.addEventListener('mouseleave', () => clearTimeout(hoverTimer));

      // 新增移动端touch点击切换分类（解决手机无hover事件）
      link.addEventListener('touchend', (e) => {
        e.preventDefault();
        clearTimeout(hoverTimer);
        links.forEach(l => l.classList.remove('is-active'));
        link.classList.add('is-active');
        const handle = link.dataset.collectionHandle;
        if (!handle) return;
        loadProducts(handle, container);
      });
    });
  }
  function bindMegaMenus() {
    document.querySelectorAll('details[is="details-mega"]').forEach(details => {
      let initialized = false;
      details.addEventListener('toggle', () => {
        if (!details.open) return; // 只在菜单展开时执行，移除isMobile阻断
        if (!initialized) {
          initialized = true;
          initDynamic(details);
        }
        // 手机点击展开菜单，自动加载第一个分类商品
        loadFirstItemImmediately(details);
      });
    });
  }
  // 页面DOM加载完成后：预缓存所有菜单首分类（全设备）
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      prefetchAllFirstCategory();
      bindMegaMenus();
    });
  } else {
    prefetchAllFirstCategory();
    bindMegaMenus();
  }
})();